/**
 * Content Sharing API Client
 *
 * Provides functions for interacting with the content sharing endpoints:
 * - Issue shares for books and chapters
 * - Manage issued and received shares
 * - Handle invitation flow
 *
 * All functions require authentication via JWT token stored in cookies.
 */

import axios from 'axios';
import { getApiUrl } from '../config';
import { getAccessToken } from './auth';
import { isEmailRecipient } from './sharesFields';

/**
 * Build authorization headers for API requests
 * @param {string} token - JWT access token
 * @returns {object} - Headers object with Authorization and Content-Type
 */
function authHeaders(token) {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Issue a new share for a book, optionally deep-linking to a chapter.
 * @param {string} fileId - UUID of the book (file) to share
 * @param {string} recipient - Username or email address of the recipient; sent
 *   as `recipient_username` or `recipient_email` accordingly
 * @param {string|null} personalMessage - Optional personal message (max 1000 chars)
 * @param {string|null} highlightChapterId - Optional chapter UUID to deep-link
 *   the recipient to on first open (must belong to fileId). Access remains
 *   book-wide regardless.
 * @returns {Promise<object>} - Share object with id, file_id, highlight_chapter_id,
 *   recipient_email, status, created_at, expires_at, recipient_account_exists
 * @throws {Error} - Throws on network error, validation error (422), an unknown
 *   username (404) or unauthorized
 */
export async function issueShare(fileId, recipient, personalMessage = null, highlightChapterId = null) {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please login first.');
  }

  const requestBody = {
    project_id: fileId,
    ...(isEmailRecipient(recipient)
      ? { recipient_email: recipient }
      : { recipient_username: recipient }),
  };

  if (personalMessage) {
    requestBody.personal_message = personalMessage;
  }
  if (highlightChapterId) {
    requestBody.highlight_chapter_id = highlightChapterId;
  }

  const response = await axios.post(
    getApiUrl('shares'),
    requestBody,
    {
      headers: authHeaders(token),
    }
  );

  return response.data;
}

/**
 * List shares issued by the current user
 * @param {string|null} fileId - Optional filter by project UUID (applied
 *   server-side)
 * @param {string|null} statusFilter - Optional comma-separated status filter
 *   (e.g., "active", "pending_acceptance", "revoked", "expired")
 * @returns {Promise<object>} - Response with `shares` array
 * @throws {Error} - Throws on network error or unauthorized
 */
export async function listIssuedShares(fileId = null, statusFilter = null) {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please login first.');
  }

  const params = {};
  if (fileId) params.project_id = fileId;
  if (statusFilter) params.status_filter = statusFilter;

  const response = await axios.get(
    getApiUrl('shares/issued'),
    {
      headers: authHeaders(token),
      params,
    }
  );

  return response.data;
}

/**
 * List shares received by the current user
 * @param {boolean} includeHidden - Whether to include hidden shares (default: false)
 * @param {'active'|'pending'|'all'} status - Which shares to return: `active`
 *   (default) — accepted and openable; `pending` — invitations awaiting a
 *   decision; `all` — both.
 * @returns {Promise<object>} - `{ projects: [...] }`; each entry carries the
 *   project fields plus `share_id` and `status`
 * @throws {Error} - Throws on network error or unauthorized
 */
export async function listReceivedShares(includeHidden = false, status = 'active') {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please login first.');
  }

  const response = await axios.get(
    getApiUrl('shares/received'),
    {
      headers: authHeaders(token),
      params: { include_hidden: includeHidden, status },
    }
  );

  return response.data;
}

/**
 * Accept or decline a pending share addressed to the current user.
 * @param {string} shareId - UUID of the share
 * @param {'accept'|'decline'} decision
 * @returns {Promise<object>} - `{ share_id, project_id, status, highlight_chapter_id }`
 * @throws {Error} - 403 (not the recipient), 404 (unknown share),
 *   410 (no longer pending), or a network error
 */
async function respondToShare(shareId, decision) {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please login first.');
  }

  const response = await axios.put(
    getApiUrl(`shares/${shareId}/${decision}`),
    null,
    { headers: authHeaders(token) }
  );

  return response.data;
}

export const acceptShare = (shareId) => respondToShare(shareId, 'accept');
export const declineShare = (shareId) => respondToShare(shareId, 'decline');

/**
 * Revoke an issued share
 * @param {string} shareId - UUID of the share to revoke
 * @returns {Promise<void>}
 * @throws {Error} - Throws on network error, unauthorized, or share not found
 */
export async function revokeShare(shareId) {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please login first.');
  }

  await axios.delete(
    getApiUrl(`shares/${shareId}`),
    {
      headers: authHeaders(token),
    }
  );
}

/**
 * Hide a received share from the library
 * @param {string} shareId - UUID of the share to hide
 * @returns {Promise<object>} - Updated share object
 * @throws {Error} - Throws on network error, unauthorized, or share not found
 */
export async function hideShare(shareId) {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please login first.');
  }

  const response = await axios.post(
    getApiUrl(`shares/${shareId}/hide`),
    {},
    {
      headers: authHeaders(token),
    }
  );

  return response.data;
}

/**
 * Unhide a previously hidden share
 * @param {string} shareId - UUID of the share to unhide
 * @returns {Promise<object>} - Updated share object
 * @throws {Error} - Throws on network error, unauthorized, or share not found
 */
export async function unhideShare(shareId) {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please login first.');
  }

  const response = await axios.post(
    getApiUrl(`shares/${shareId}/unhide`),
    {},
    {
      headers: authHeaders(token),
    }
  );

  return response.data;
}

/**
 * Get invitation details (anonymous - no auth required)
 * @param {string} token - Invitation token from URL
 * @returns {Promise<object>} - Invitation details including shared content info
 * @throws {Error} - Throws on network error or invalid token
 */
export async function getInvitationDetails(token) {
  const response = await axios.get(
    getApiUrl(`invitations/${token}`)
  );

  return response.data;
}

/**
 * Accept an invitation (for existing logged-in users)
 * @param {string} token - Invitation token from URL
 * @returns {Promise<object>} - Acceptance confirmation with share details
 * @throws {Error} - Throws on network error, unauthorized, expired token, or already accepted
 */
export async function acceptInvitation(token) {
  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('No access token found. Please login first.');
  }

  const response = await axios.post(
    getApiUrl(`invitations/${token}/accept`),
    {},
    {
      headers: authHeaders(accessToken),
    }
  );

  return response.data;
}

/**
 * Register a new user via share invitation token
 * @param {string} username - Username for the new account
 * @param {string} email - Email address for the new account
 * @param {string} password - Password for the new account
 * @param {string} shareInvitationToken - Share invitation token from URL
 * @returns {Promise<object>} - Registration response with access_token
 * @throws {Error} - Throws on network error, validation error, or invalid token
 */
export async function registerViaInvitation(username, email, password, shareInvitationToken) {
  const response = await axios.post(
    getApiUrl('shares/register'),
    {
      username,
      email,
      password,
      share_invitation_token: shareInvitationToken,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
}
